import React, { useCallback, useEffect, useState } from 'react';
import { addWeeks, format, startOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CalendarRange, ChevronLeft, ChevronRight, Loader2, Printer } from 'lucide-react';
import { getWeeklyPlan, getWeeklyPlanPrefs, saveWeeklyPlanPrefs } from '../../lib/api';
import { printWeeklyPlan } from '../../lib/weeklyPlanPrint';
import { WeeklyPlanControls } from './WeeklyPlanControls';
import { WeekDayStrip } from './WeekDayStrip';
import { WeekProductGrid } from './WeekProductGrid';
import { WeekOrdersByDay } from './WeekOrdersByDay';

const monday = (d) => startOfWeek(d, { weekStartsOn: 1 });

const usePrefs = () => {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    getWeeklyPlanPrefs().then(({ data }) => setPrefs(data)).catch(() => setPrefs({ layout: 'both', date_field: 'dispatch', include: 'all', weeks: 1, saved: false }));
  }, []);
  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs({ ...next, saved: true });
    const { saved, ...body } = next;
    saveWeeklyPlanPrefs(body).catch(() => toast.error('Could not save your weekly plan settings'));
  };
  return [prefs, update];
};

const NavBar = ({ setAnchor, onPrint, loading, canPrint }) => (
  <div className="flex items-center gap-1.5">
    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAnchor((a) => addWeeks(a, -1))} data-testid="wp-prev"><ChevronLeft className="w-4 h-4" /></Button>
    <Button variant="outline" size="sm" className="h-8" onClick={() => setAnchor(monday(new Date()))} data-testid="wp-this-week">This week</Button>
    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAnchor((a) => addWeeks(a, 1))} data-testid="wp-next"><ChevronRight className="w-4 h-4" /></Button>
    <Button size="sm" className="h-8 ml-1 bg-[#005F73] hover:bg-[#0A9396]" onClick={onPrint} disabled={!canPrint || loading} data-testid="wp-print">
      {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Printer className="w-4 h-4 mr-1.5" />}Print
    </Button>
  </div>
);

export const WeeklyPlanCard = () => {
  const [prefs, updatePrefs] = usePrefs();
  const [anchor, setAnchor] = useState(monday(new Date()));
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    if (!prefs) return;
    setLoading(true);
    try {
      const { data } = await getWeeklyPlan({ start: format(anchor, 'yyyy-MM-dd'), weeks: prefs.weeks, date_field: prefs.date_field, include: prefs.include });
      setPlan(data);
      setActiveWeek((i) => Math.min(i, data.weeks.length - 1));
    } catch {
      toast.error('Could not load the weekly plan');
    } finally {
      setLoading(false);
    }
  }, [anchor, prefs?.weeks, prefs?.date_field, prefs?.include]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedDay(null); }, [anchor]);

  const week = plan?.weeks?.[activeWeek];
  return (
    <Card data-testid="weekly-plan-card">
      <CardHeader className="pb-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2"><CalendarRange className="w-4 h-4 text-[#005F73]" />Weekly plan · units by day</CardTitle>
          <NavBar setAnchor={setAnchor} loading={loading} canPrint={!!plan} onPrint={() => printWeeklyPlan(plan, prefs)} />
        </div>
        {prefs && <WeeklyPlanControls prefs={prefs} onChange={updatePrefs} />}
        {prefs && !prefs.saved && <p className="text-xs text-amber-700" data-testid="wp-first-time-hint">Pick how you'd like your plan shown. Your choices are saved for you.</p>}
      </CardHeader>
      <CardContent className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60' : ''}`}>
        {!plan ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <>
            <div className="space-y-1.5 overflow-x-auto">
              {plan.weeks.map((w, i) => (
                <WeekDayStrip key={w.start} week={w} active={i === activeWeek}
                  onActivate={() => { if (i !== activeWeek) { setActiveWeek(i); setSelectedDay(null); } }}
                  selectedDay={selectedDay} onSelectDay={setSelectedDay} />
              ))}
            </div>
            {plan.unscheduled > 0 && <p className="text-xs text-amber-700" data-testid="wp-unscheduled">{plan.unscheduled} open order(s) have no {plan.date_field === 'delivery' ? 'delivery' : 'dispatch'} date and aren&apos;t shown.</p>}
            {week && (
              <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1" data-testid="wp-details">
                {prefs.layout !== 'orders' && !selectedDay && <WeekProductGrid week={week} />}
                {(prefs.layout !== 'grid' || selectedDay) && <WeekOrdersByDay week={week} onlyDay={selectedDay} />}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
